// client/src/pages/admin/AdminEvents.jsx

import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import Swal from 'sweetalert2';

const API_BASE = process.env.REACT_APP_BASE_URL || "http://localhost:5000/";

const AdminEvents = () => {
  // ─── State ──────────────────────────────────────────────────────────────────
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal controls:
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);

  // Form data:
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    location: '',
    image: null,
    imagePreview: '',
    order: 0,
    isActive: true
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // ─── On Mount: fetch events ──────────────────────────────────────────────
  useEffect(() => {
    fetchEvents();
  }, []);

  // ─── Fetch All Events ───────────────────────────────────────────────────
  const fetchEvents = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get('/events');
      setEvents(data);
    } catch (err) {
      console.error('Error loading events:', err);
      Swal.fire('Error', 'Failed to load events', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Open "Add New Event" Modal ───────────────────────────────────────────
  const openAddModal = () => {
    setEditMode(false);
    setCurrentEvent(null);
    setFormData({
      title: '',
      description: '',
      date: '',
      location: '',
      image: null,
      imagePreview: '',
      order: 0,
      isActive: true
    });
    setIsModalOpen(true);
  };

  // ─── Open "Edit Event" Modal ──────────────────────────────────────────────
  const openEditModal = (event) => {
    setEditMode(true);
    setCurrentEvent(event);

    // Pre‐fill the form with existing data
    const imagePreviewUrl = event.image_path
      ? event.image_path.startsWith('http')
        ? event.image_path
        : `${API_BASE}event.image_path`
      : '';

    setFormData({
      title: event.title || '',
      description: event.description || '',
      date: event.date ? new Date(event.date).toISOString().split('T')[0] : '',
      location: event.location || '',
      image: null,
      imagePreview: imagePreviewUrl,
      order: event.order || 0,
      isActive: event.isActive !== false
    });
    setIsModalOpen(true);
  };

  // ─── Close Modal & Reset ─────────────────────────────────────────────────────
  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentEvent(null);
    setFormData({
      title: '',
      description: '',
      date: '',
      location: '',
      image: null,
      imagePreview: '',
      order: 0,
      isActive: true
    });
  };

  // ─── Handle Form Changes ─────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(fd => ({
      ...fd,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // ─── Handle Image Selection ───────────────────────────────────────────────────
  const handleImageChange = (e) => {
    if (!e.target.files?.[0]) return;
    const file = e.target.files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      Swal.fire('Error', 'Please select an image file', 'error');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire('Error', 'Image size should be less than 5MB', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData(fd => ({
        ...fd,
        image: file,
        imagePreview: e.target.result
      }));
    };
    reader.readAsDataURL(file);
  };

  // ─── Upload Single File ─────────────────────────────────────────────────────
  const uploadSingleFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await api.post('/upload/file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return data.path;
  };

  // ─── Handle Form Submit ─────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      Swal.fire('Error', 'Title is required', 'error');
      return;
    }

    if (!formData.date) {
      Swal.fire('Error', 'Date is required', 'error');
      return;
    }

    if (!editMode && !formData.image) {
      Swal.fire('Error', 'Image is required for new events', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      let imagePath = '';

      // Upload image if selected
      if (formData.image) {
        imagePath = await uploadSingleFile(formData.image);
      }

      const eventData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        date: formData.date,
        location: formData.location.trim(),
        order: parseInt(formData.order) || 0,
        isActive: formData.isActive
      };

      if (imagePath) eventData.image_path = imagePath;

      if (editMode) {
        // Update existing event
        await api.put(`/events/${currentEvent.id}`, eventData);
        Swal.fire('Success', 'Event updated successfully', 'success');
      } else {
        // Create new event
        await api.post('/events', eventData);
        Swal.fire('Success', 'Event created successfully', 'success');
      }

      closeModal();
      fetchEvents();
    } catch (err) {
      console.error('Error saving event:', err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to save event', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Handle Delete ───────────────────────────────────────────────────────────
  const handleDelete = (event) => {
    Swal.fire({
      title: 'Delete Event',
      text: `Are you sure you want to delete "${event.title}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/events/${event.id}`);
          Swal.fire('Deleted!', 'Event has been deleted.', 'success');
          fetchEvents();
        } catch (err) {
          console.error('Error deleting event:', err);
          Swal.fire('Error', 'Failed to delete event', 'error');
        }
      }
    });
  };

  // ─── Toggle Active Status ───────────────────────────────────────────────────
  const toggleActive = async (event) => {
    try {
      await api.patch(`/events/${event.id}`, {
        isActive: !event.isActive
      });
      fetchEvents();
    } catch (err) {
      console.error('Error toggling status:', err);
      Swal.fire('Error', 'Failed to update status', 'error');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Events Management</h1>
        <button
          onClick={openAddModal}
          className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
        >
          Add New Event
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {events.length > 0 ? (
                  events.map((event) => (
                    <tr key={event.id} className="hover:bg-gray-50">
                      {/* Event Info */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-16 w-24">
                            {event.image_path ? (
                              <img
                                className="h-16 w-24 rounded-lg object-cover"
                                src={
                                  event.image_path.startsWith('http')
                                    ? event.image_path
                                    : `${API_BASE}event.image_path`
                                }
                                alt={event.title}
                              />
                            ) : (
                              <div className="h-16 w-24 rounded-lg bg-gray-200 flex items-center justify-center">
                                <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {event.title}
                            </div>
                            <div className="text-sm text-gray-500">
                              {event.description}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Date */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(event.date).toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </div>
                      </td>

                      {/* Location */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {event.location || 'N/A'}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => toggleActive(event)}
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            event.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {event.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => openEditModal(event)}
                          className="text-black hover:text-gray-700"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(event)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                      No events found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold mb-4">
              {editMode ? 'Edit Event' : 'Add New Event'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  required
                />
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Image {!editMode && '*'}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  required={!editMode}
                />
                {formData.imagePreview && (
                  <img
                    src={formData.imagePreview}
                    alt="Preview"
                    className="mt-2 h-20 w-32 object-cover rounded-lg"
                  />
                )}
              </div>

              {/* Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Order
                </label>
                <input
                  type="number"
                  name="order"
                  value={formData.order}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>

              {/* Active Status */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Active
                </label>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEvents;
